import { DynamicTable } from "../../../../components/Table/DynamicTable";
import { CountType } from "../../../../types/Count";
import { Direction } from "../../../../types/Direction";
import { ColumnDef, RowData } from "@tanstack/react-table";
import { useMemo } from "react";
import { Device } from "../../../../types/Device";
import { Mode } from "../../../../types/Mode";

/** A map of counts, keyed first by
 * the CountType, followed by device name,
 * followed by direction, which yields the count.
 */
interface DeviceCounts {
    device: Device;
    count: Record<
        Direction,
        {
            [CountType.LowerBound]?: number | undefined | null;
            [CountType.Adjusted]: number | undefined | null;
            [CountType.UpperBound]?: number | undefined | null;
            [CountType.Predicted]?: number | undefined | null;
        }
    >;
    timestamp: Date;
    // Per-device CI eligibility. When the global CI toggle is on but this
    // device is below the 30-manual-count threshold, its LowerBound and
    // UpperBound cells render "-" and its Adjusted row falls back to the
    // Predicted value.
    ciEligible?: boolean;
}

// Extend the ColumnMeta interface with a function to apply conditional styling.
declare module "@tanstack/react-table" {
    interface ColumnMeta<TData extends RowData, TValue> {
        getStyle?: (cellValue: any, validatedOn: Date) => React.CSSProperties;
    }
}

const initializeColumns = (data: DeviceCounts[], enableCI: boolean): ColumnDef<Row, any>[] => {
    const columns: ColumnDef<Row, any>[] = [];

    if (enableCI) {
        columns.push({
            accessorKey: "type",
            cell: ({ cell }) => {
                let typeStr = cell.getValue().replace(/([a-z])([A-Z])/g, "$1 $2");
                typeStr = typeStr.replace(/([A-Z])([A-Z][a-z])/g, "$1 $2");
                return typeStr;
            },
            header: () => <span></span>,
        });
    }

    type OrderKey = Direction | Mode;

    const order: Record<OrderKey, number> = {
        [Direction.Upstream]: 0,
        [Direction.Downstream]: 1,
        [Direction.Net]: 2,
        [Mode.VideoOnly]: 3,
    };

    data.forEach((deviceCount) => {
        let directions: string[] = [];
        if (deviceCount.count) {
            directions = Object.keys(deviceCount.count).sort((a, b): number => {
                return order[a as OrderKey] - order[b as OrderKey];
            });
        }

        const deviceCols: ColumnDef<Row, any>[] = [];
        directions.forEach((direction: string) => {
            deviceCols.push({
                accessorFn: (row) => {
                    const value = row[`${deviceCount.device.name}-${direction}`];
                    if (
                        value &&
                        typeof value === "object" &&
                        (typeof value.value === "number" || value.value === null || value.value === undefined)
                    ) {
                        return value;
                    }
                    return undefined;
                },
                id: `${deviceCount.device.name}-${direction}`,
                cell: (info) => {
                    const value = info.getValue();
                    const style = info.column.columnDef.meta?.getStyle?.(
                        value,
                        new Date(deviceCount.device.accurateAsOf),
                    );
                    return (
                        <span style={style} aria-label={`${deviceCount.device.name}-${direction}`}>
                            {/* Show "-" if value is null or undefined, else show the value */}
                            {value?.value === undefined || value?.value === null ? "-" : value.value}
                        </span>
                    );
                },
                header: () => {
                    const isUnidirectional = deviceCount.device.configs?.[0].directionality === "UNIDIRECTIONAL";
                    if (isUnidirectional) {
                        return <span>{`Counts`}</span>;
                    }
                    return <span>{`${direction}`}</span>;
                },
                meta: {
                    getStyle: (cellValue: { created: Date } | undefined, validatedOn: Date) =>
                        cellValue?.created && cellValue.created >= validatedOn
                            ? { color: "inherit" }
                            : { color: "#CCCCCC" },
                },
            });
        });

        columns.push({
            header: deviceCount.device.displayName,
            columns: deviceCols,
        });
    });

    return columns;
};

interface Row {
    type: string;
    [key: string]:
        | string
        | {
              value: number | null;
              created: Date;
          };
}

const formatData = (data: DeviceCounts[], enableCI: boolean): Row[] => {
    const typedRows: Record<CountType, Row> = {
        [CountType.LowerBound]: { type: CountType.LowerBound },
        [CountType.Adjusted]: { type: CountType.Adjusted },
        [CountType.UpperBound]: { type: CountType.UpperBound },
        [CountType.Predicted]: { type: CountType.Predicted },
    };

    data.forEach((deviceCount) => {
        // When the global CI toggle is on but this device doesn't meet the
        // manual-count threshold, suppress its bound values and surface
        // Predicted in the Adjusted row instead — that way the device's
        // column still has data to show without implying false precision.
        const suppressCIForDevice = enableCI && deviceCount.ciEligible === false;

        Object.keys(deviceCount.count).forEach((key) => {
            if (!Object.values<string>(Direction).includes(key) && key !== Mode.VideoOnly) {
                return;
            }

            Object.keys(deviceCount.count[key as keyof typeof deviceCount.count] || {}).forEach((type) => {
                const directionCounts = deviceCount.count[key as keyof typeof deviceCount.count];
                let value: number | string | null | undefined = directionCounts[type as keyof typeof CountType];

                if (suppressCIForDevice) {
                    if (type === CountType.LowerBound || type === CountType.UpperBound) {
                        // Render as "-" — the cell formatter treats
                        // null/undefined as "no data".
                        typedRows[type as CountType][`${deviceCount.device.name}-${key}`] = {
                            value: null,
                            created: deviceCount.timestamp,
                        };
                        return;
                    }
                    if (type === CountType.Adjusted) {
                        value = directionCounts[CountType.Predicted];
                    }
                }

                typedRows[type as CountType][`${deviceCount.device.name}-${key}`] = {
                    value: value ?? 0,
                    created: deviceCount.timestamp,
                };
            });
        });
    });

    return Object.values(typedRows);
};

export interface Properties {
    data: DeviceCounts[];
    showCI: boolean;
    label: string;
}

/** Component that displays a high-level summary of the season's
 * Directional Counts.
 *
 * If props.showCI is true, then all confidence bounds will be displayed
 * in their own row. If false, then only the Predicted counts
 * will be displayed.
 *
 * Data state is expected to be controlled by the parent.
 */
export const SummaryTable = (props: Properties) => {
    const columns = useMemo<ColumnDef<Row, any>[]>(
        () => initializeColumns(props.data, props.showCI),
        [props.data, props.showCI],
    );

    const display: string[] = [];
    if (props.showCI) {
        display.push(CountType.Adjusted as string);
        display.push(CountType.LowerBound as string);
        display.push(CountType.UpperBound as string);
    }

    const filteredData = formatData(props.data, props.showCI)?.filter(
        (d) =>
            (props.showCI && display.includes(d.type)) || (!props.showCI && d.type === (CountType.Predicted as string)),
    );

    if (filteredData.length === 0) {
        return;
    }

    return <DynamicTable columns={columns} data={filteredData} label={props.label} />;
};
